import type { UIMessage } from 'ai';
import { isToolUIPart, getToolName } from 'ai';

export type QuotePreview = {
    id: string;
    role: UIMessage['role'];
    excerpt: string;
};

const MAX_PREVIEW_LENGTH = 140;

/**
 * Build a short preview string for the pending-quote chip rendered above the
 * composer. Plain text turns get the first ~140 characters; assistant turns
 * whose only useful content is a `search_activities` tool call get a
 * deterministic "Tool result: N activities" label so the chip never collapses
 * to an empty string.
 */
export function extractQuotePreview(message: UIMessage): QuotePreview {
    const text = collectTextFromParts(message);
    if (text.length > 0) {
        return {
            id: message.id,
            role: message.role,
            excerpt: truncate(text, MAX_PREVIEW_LENGTH),
        };
    }

    for (const part of message.parts ?? []) {
        if (!isToolUIPart(part)) continue;
        if (part.state !== 'output-available') continue;
        if (getToolName(part) !== 'search_activities') continue;
        const output = (part as { output?: unknown }).output;
        const count = readToolResultCount(output);
        return {
            id: message.id,
            role: message.role,
            excerpt: `Tool result: ${count} activities`,
        };
    }

    return { id: message.id, role: message.role, excerpt: '' };
}

/**
 * Assemble the additional `body` payload sent alongside a quote-reply user
 * turn. Kept as a tiny pure helper so the request shape is unit-testable
 * without booting the AI SDK transport.
 */
export function buildReplyRequestBody(args: {
    model: string;
    chatId: string;
    quotedMessage: UIMessage;
    locale?: 'nl' | 'fr' | 'en';
    /** Full site locale (e.g. "nl-BE") — lets the server build localized PDP urls. */
    siteLocale?: string;
}): {
    model: string;
    chatId: string;
    quotedMessage: UIMessage;
    locale?: 'nl' | 'fr' | 'en';
    siteLocale?: string;
} {
    return {
        model: args.model,
        chatId: args.chatId,
        quotedMessage: args.quotedMessage,
        ...(args.locale !== undefined ? { locale: args.locale } : {}),
        ...(args.siteLocale !== undefined ? { siteLocale: args.siteLocale } : {}),
    };
}

function collectTextFromParts(message: UIMessage): string {
    return (message.parts ?? [])
        .filter(
            (p): p is { type: 'text'; text: string } =>
                p?.type === 'text' && typeof (p as { text?: unknown }).text === 'string',
        )
        .map(p => p.text)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function truncate(value: string, max: number): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max - 1).trimEnd()}…`;
}

function readToolResultCount(output: unknown): number {
    if (output && typeof output === 'object' && 'count' in output) {
        const count = (output as { count: unknown }).count;
        if (typeof count === 'number') return count;
    }
    return 0;
}
